import type { ReactNode } from "react";

import { Box, Divider, IconButton, Typography } from "@luce/ui";

export type PageHeaderProps = {
  /** Title text shown next to the back button. */
  title: string;
  /** When provided, renders the circular back button and fires this on press. */
  onBack?: () => void;
  /** Render a divider line below the header. */
  divider?: boolean;
  /** Optional node rendered at the trailing (end) edge of the header. */
  trailing?: ReactNode;
};

/**
 * Page-level header (app bar): optional back button + title, optional trailing
 * slot, and an optional divider beneath. Stateless — composes @luce/ui atoms.
 *
 * The back button uses the DS `neutral` IconButton variant ("CTA Neutral"):
 * a white, neutral-bordered circle with a neutral-foreground arrow.
 */
export function PageHeader({ title, onBack, divider = true, trailing }: PageHeaderProps) {
  return (
    <Box className="gap-spacing-xl bg-luce-neutral-background-default flex flex-col">
      <Box className="gap-spacing-md flex min-h-[40px] flex-row items-center">
        {onBack && (
          <IconButton
            variant="neutral"
            size="md"
            onClick={onBack}
            icon="ArrowLeft"
            accessibilityLabel="Back"
            className="w-10 md:w-14"
          />
        )}
        {/* The screen's title on desktop: its page heading (NavHeader's is the mobile one) */}
        <Typography
          variant="label-md"
          color="neutral-foreground-default"
          accessibilityRole="header"
          as="h1"
          className="flex-1"
        >
          {title}
        </Typography>
        {trailing}
      </Box>
      {divider && <Divider />}
    </Box>
  );
}
